import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";

// Route-level code splitting: only the homepage ships in the main bundle (D5.4).
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const WhoWeServe = lazy(() => import("@/pages/WhoWeServe"));
const Government = lazy(() => import("@/pages/Government"));
const Contact = lazy(() => import("@/pages/Contact"));
const Survey = lazy(() => import("@/pages/Survey"));
const ProfessorMentale = lazy(() => import("@/pages/ProfessorMentale"));
const MindLab = lazy(() => import("@/pages/MindLab"));
const MindLabPractice = lazy(() => import("@/pages/MindLabPractice"));
const Lesson = lazy(() => import("@/pages/Lesson"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Admin = lazy(() => import("@/pages/Admin"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentSuccess })));
const PaymentCancel = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancel })));
const Pricing = lazy(() => import("@/pages/Pricing"));
const RequestProposal = lazy(() => import("@/pages/RequestProposal"));
const Verify = lazy(() => import("@/pages/Verify"));
const Faq = lazy(() => import("@/pages/Faq"));
const Category = lazy(() => import("@/pages/Category"));
const Compare = lazy(() => import("@/pages/Compare"));

const PageFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center" aria-busy="true">
    <div className="w-8 h-8 border-2 border-navy/20 border-t-navy rounded-full animate-spin" />
  </div>
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="relative z-10">
        <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/who-we-serve" element={<WhoWeServe />} />
          <Route path="/government-contracting" element={<Government />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/survey" element={<Survey />} />
          <Route path="/professor-mentale" element={<ProfessorMentale />} />
          <Route path="/mind-lab" element={<MindLab />} />
          <Route path="/mind-lab/practice" element={<MindLabPractice />} />
          <Route path="/learn/:id" element={<Lesson />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/request-proposal" element={<RequestProposal />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/verify/:id" element={<Verify />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/behavioral-health-education-for-veterans-and-families" element={<Category />} />
          <Route path="/vs/:slug" element={<Compare />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
